<template>
  <div class="container mt-4">
    <TopNav v-if="showNav" :showOpenMainNav="true" />
    <div class="content-container mx-auto">
      <div class="row mt-5 justify-content-between">
        <div class="col-12 col-md-auto">
          <div class="d-flex flex-wrap" v-if="subscriptionType">
            <h2 class="p-0 m-0">{{ title }}</h2>
          </div>
        </div>
        <div
          class="col-12 col-md-auto d-flex justify-content-md-end mt-3 mt-md-0"
        >
          <HMSwitchTabs :selected.sync="subscriptionType">
            <HMSwitchTab name="monthly">
              {{ $t('SIGNUP.LABEL.MONTHLY') }}
            </HMSwitchTab>
            <HMSwitchTab name="yearly">
              {{ $tc('SIGNUP.LABEL.ANNUAL_SAVE_20') }}
            </HMSwitchTab>
          </HMSwitchTabs>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="plans">
            <PlanCardV2
              @click="selectPlan(plan)"
              :value="selectedPlan === plan"
              :plan="plan"
              v-for="plan in plansData"
              :key="plan.id"
            />
          </div>
        </div>
      </div>
      <div class="row justify-content-between align-items-end mt-4">
        <div class="col-12 col-md-auto">
          <div v-if="!(selectedPlan && selectedPlan.hideCoupon)">
            <a
              class="link cursor-pointer"
              @click="showCoupon = !showCoupon"
              v-if="!showCoupon"
            >
              {{ $t('SIGNUP.BUTTON.ADD_COUPON_CODE') }}
            </a>
            <div class="form-group mb-0" v-if="showCoupon">
              <div class="d-flex" :class="{ 'is-invalid': couponError }">
                <input
                  type="text"
                  :placeholder="`${$t('SIGNUP.LABEL.COUPON_CODE')}`"
                  class="form-control"
                  name="coupon"
                  id="coupon"
                  v-model="couponCode"
                  @change="removeCoupon"
                />
                <span
                  class="glyphicon glyphicon-ok form-control-feedback"
                  v-if="usingCoupon"
                  aria-hidden="true"
                ></span>
                <HMButton
                  class="ml-3"
                  :disabled="!couponCode"
                  color="outline-light-gray"
                  @click="applyCoupon"
                >
                  {{ $t('SIGNUP.BUTTON.APPLY') }}
                  <span
                    v-if="couponApplied"
                    class="c-forrest"
                    style="margin-left: 5px"
                  >
                    <i class="fa fa-check c-light-green"></i>
                  </span>
                </HMButton>
              </div>
              <span class="invalid-feedback" v-if="couponError">
                {{ couponError }}
              </span>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-auto mt-3 mt-md-0">
          <HMButton
            class="btn btn-primary"
            @click="upgrade"
            :disabled="!selectedPlan"
          >
            {{ buttonText }}
          </HMButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Vue from 'vue';

import { getUpgradePlansData, getEnterprisePlanData } from '@/web/utils/plans';
import PlanCardV2 from '@/web/components/signup/PlanCardV2.vue';
import UpgradePlanModal from '@/web/components/settings/UpgradePlanModal.vue';
import TopNav from '@/web/components/navigation/TopNav';
import { getConfig } from '@/web/utils/configuration';
import { sanitize } from '@/core/utils/utils';

export default Vue.extend({
  components: { PlanCardV2, TopNav },
  props: {
    showNav: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      subscriptionType: 'monthly',
      couponCode: '',
      usingCoupon: false,
      couponError: false,
      showCoupon: false,
      selectedPlanData: null,
    };
  },
  computed: {
    ...mapGetters({
      affiliateCode: 'globals/affiliateCode',
      userPlans: 'user/userPlans',
      teacher: 'user/teacher',
      couponDuration: 'user/couponDuration',
      coupon: 'user/coupon',
    }),
    title() {
      return this.teacher.is_expired
        ? this.$t('SIGNUP.TITLE.REACTIVATE_PLAN')
        : this.$t('SIGNUP.TITLE.CHOOSE_A_PLAN');
    },
    buttonText() {
      if (this.selectedPlan?.id === 'enterprise-plan') {
        return this.$t('SIGNUP.BUTTON.CONTACT_US');
      } else if (this.teacher.is_expired) {
        return this.$t('SIGNUP.BUTTON.REACTIVATE_NOW');
      } else {
        return this.$t('SIGNUP.BUTTON.UPGRADE_NOW');
      }
    },
    selectedPlan() {
      if (!this.selectedPlanData) return null;
      return this.plansData.find(
        plan =>
          plan.id === this.selectedPlanData.id &&
          plan.subscriptionType === this.selectedPlanData.subscriptionType
      );
    },
    config() {
      return getConfig();
    },
    plansData() {
      if (!this.teacher || !this.userPlans) return [];
      const enterprisePlan = getEnterprisePlanData();

      const plans = [...this.userPlans]
        .filter(plan => !(plan.hide_for_affiliates && this.affiliateCode))
        .sort((planA, planB) => planA.tier - planB.tier)
        .map(plan =>
          getUpgradePlansData(
            plan,
            this.subscriptionType,
            this.teacher,
            this.couponDuration
          )
        )
        .filter(({ status }) => !['lower-tier', 'current'].includes(status));
      return [...plans, enterprisePlan];
    },
    couponApplied() {
      return this.plansData.some(({ discounted }) => discounted);
    },
  },
  methods: {
    sanitize,
    removeCoupon() {
      this.$store.dispatch('user/clearCoupon');
    },
    async applyCoupon() {
      try {
        const coupon = this.couponCode;
        this.couponError = '';
        await this.$store.dispatch('user/applyCoupon', {
          coupon,
          campaign: this.teacher.plans_campaign,
        });
        this.usingCoupon = true;
      } catch (error) {
        this.couponError = error.response.data?.message;
        this.usingCoupon = false;
      }
    },
    selectPlan(plan) {
      this.selectedPlanData = plan;
    },
    upgrade() {
      if (this.selectedPlan.id === 'enterprise-plan') {
        this.openIntercomChat();
      } else {
        this.$modal.show(UpgradePlanModal, {
          plan: this.selectedPlan,
          subscriptionType: this.subscriptionType,
          coupon: this.couponApplied ? this.couponCode : null,
        });
      }
    },
    openIntercomChat() {
      const message =
        "Hi, I'm interested in learning more about Enterprise plan.";
      window.Intercom('showNewMessage', message);
    },
  },
  async mounted() {
    await this.$store.dispatch('user/getPlans', this.teacher.plans_campaign);
    this.subscriptionType = this.teacher?.subscription_interval || 'monthly';
    this.selectedPlanData = this.plansData.find(
      plan => plan.isActive || plan.status === 'reactivate'
    );
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.content-container {
  max-width: 560px;
}

.plans {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  flex-direction: column;
  flex-wrap: wrap;
}
</style>
